/**
 * KlingOptionsPanel - Collapsible settings panel for Kling AI video generation.
 * Shows model selection, duration, aspect ratio, and cost estimate.
 */

import { Wand2, ChevronDown, ChevronUp, Monitor, Smartphone, Square } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import {
  KLING_MODEL_OPTIONS,
  KLING_DURATIONS,
  KLING_ASPECT_RATIOS,
  DEFAULT_KLING_CONFIG,
  calculateKlingTokens,
  type KlingConfig,
} from '@/constants/klingVideo';
import { SegmentedPicker } from '../../common/SegmentedPicker';
import type { SegmentOption } from '../../common/SegmentedPicker';

interface KlingOptionsPanelProps {
  klingConfig: KlingConfig;
  /** Upstream USD cost of one clip, or `null` when the configuration has no price. */
  estimatedCost: number | null;
  durationMinutes: number;
  showAdvanced: boolean;
  onToggleAdvanced: () => void;
  onChange: (config: KlingConfig) => void;
  disabled: boolean;
  /** Hide cost estimate (e.g. when parent already shows unified cost) */
  showCost?: boolean;
}

const ASPECT_RATIO_ICONS: Record<string, JSX.Element> = {
  '16:9': <Monitor className="h-4 w-4" />,
  '9:16': <Smartphone className="h-4 w-4" />,
  '1:1': <Square className="h-4 w-4" />,
};

export function KlingOptionsPanel({
  klingConfig,
  estimatedCost: _estimatedCost,
  durationMinutes,
  showAdvanced,
  onToggleAdvanced,
  onChange,
  disabled,
  showCost = true,
}: KlingOptionsPanelProps) {
  const { t } = useTranslation();

  const modelOptions: SegmentOption[] = KLING_MODEL_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
    description: option.description,
    trailingMeta: `${option.tokens10s.toLocaleString()} tokens/10s`,
  }));

  const durationOptions: SegmentOption[] = KLING_DURATIONS.map((d) => ({
    value: d,
    label: `${d}s`,
  }));

  const aspectRatioOptions: SegmentOption[] = KLING_ASPECT_RATIOS.map((ratio) => ({
    value: ratio,
    label: ratio,
    iconNode: ASPECT_RATIO_ICONS[ratio],
  }));

  const currentModel = klingConfig.model ?? DEFAULT_KLING_CONFIG.model;
  const currentDuration = klingConfig.duration ?? DEFAULT_KLING_CONFIG.duration;
  const currentAspectRatio = klingConfig.aspect_ratio ?? DEFAULT_KLING_CONFIG.aspect_ratio;

  return (
    <div className="border border-white/10 rounded-lg overflow-hidden bg-black/20">
      <button
        type="button"
        onClick={onToggleAdvanced}
        className="w-full flex items-center justify-between p-3 bg-black/20 hover:bg-white/5 transition-colors"
      >
        <span className="font-medium text-white/80 flex items-center space-x-2">
          <Wand2 className="h-4 w-4 text-primary-400" />
          <span>{t('videoSource.klingSettings', { defaultValue: 'Kling AI Settings' })}</span>
        </span>
        {showAdvanced ? (
          <ChevronUp className="w-5 h-5 text-white/40" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white/40" />
        )}
      </button>

      {showAdvanced && (
        <div className="p-4 space-y-4 border-t border-white/5">
          {/* Quality Level */}
          <SegmentedPicker
            label={t('videoSource.klingModel', { defaultValue: 'Quality Level' })}
            options={modelOptions}
            value={currentModel}
            onChange={(val) => onChange({ ...klingConfig, model: val as KlingConfig['model'] })}
            size="md"
            layout="equal"
            disabled={disabled}
          />

          {/* Clip Duration */}
          <SegmentedPicker
            label={t('videoSource.klingDuration', { defaultValue: 'Clip Duration' })}
            options={durationOptions}
            value={currentDuration}
            onChange={(val) =>
              onChange({ ...klingConfig, duration: val as KlingConfig['duration'] })
            }
            size="sm"
            layout="equal"
            disabled={disabled}
          />

          {/* Aspect Ratio */}
          <SegmentedPicker
            label={t('videoSource.klingAspectRatio', { defaultValue: 'Aspect Ratio' })}
            options={aspectRatioOptions}
            value={currentAspectRatio}
            onChange={(val) =>
              onChange({ ...klingConfig, aspect_ratio: val as KlingConfig['aspect_ratio'] })
            }
            size="sm"
            layout="equal"
            disabled={disabled}
          />

          {/* Cost Estimate */}
          {showCost && (
            <div className="mt-4 text-right">
              <div className="text-xs text-white/40">
                {t('videoSource.estimatedCost', { defaultValue: 'Cost' })}
              </div>
              <div className="text-lg font-semibold text-green-400">
                {(() => {
                  const clipDuration = parseInt(currentDuration);
                  const numClips = Math.ceil((durationMinutes * 60) / clipDuration);
                  const tokensPerClip = calculateKlingTokens(currentDuration, currentModel);
                  return (numClips * tokensPerClip).toLocaleString();
                })()}{' '}
                tokens
              </div>
              <div className="text-xs text-white/40 mt-0.5">
                {(() => {
                  const clipDuration = parseInt(currentDuration);
                  const numClips = Math.ceil((durationMinutes * 60) / clipDuration);
                  const tokensPerClip = calculateKlingTokens(currentDuration, currentModel);
                  return `${numClips} clips x ${tokensPerClip.toLocaleString()} tokens`;
                })()}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
